import React, { createContext, useEffect } from 'react';
import z from 'zod';
import { BrandbookData } from '../types';


// Form Schema
export const BrandBookFormSchema = z.object({
  companyName: z.string().min(1, 'Brand name is required'),
  industry: z.string().min(1, 'Industry is required'),
  differentiators: z.string().min(1, 'Differentiator is required'),
  description: z.string().min(1, 'Description is required'),
  age: z.array(z.number()).default([20, 80]),
  gender: z.string().min(1, 'Gender is required'),
  location: z.string().min(1, 'Location is required'),
  logoType: z.enum(['generate', 'upload']),
  targetAudience: z.string().min(1, 'Target audience is required'),
});

// Form data type with optional brandbook
export type TBrandBookFormData = z.infer<typeof BrandBookFormSchema> & {
  brandbook?: BrandbookData;
};

// Context type
type BrandBookContextType = {
  formDetails: TBrandBookFormData;
  setFormDetails: (data: TBrandBookFormData) => void;
  updateBrandbook: (newBrandbook: BrandbookData) => void;
  updateBrandbookSection: (section: string, content: any) => void;
};

// Create context
export const BrandBookContext = createContext<BrandBookContextType | undefined>(
  undefined
);

// Context hook
export const useBrandBookContext = () => {
  const context = React.useContext(BrandBookContext);
  if (context === undefined) {
    throw new Error(
      'useBrandBookContext must be used within a BrandBookContextProvider'
    );
  }
  return context;
};

// Context provider
export const BrandBookContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [formDetails, setFormDetails] = React.useState<TBrandBookFormData>({
    companyName: '',
    industry: '',
    differentiators: '',
    description: '',
    age: [20, 80],
    gender: '',
    location: '',
    logoType: 'generate',
    targetAudience: '',
  });

  // Check for saved brandbook data in localStorage on initial load
  useEffect(() => {
    const savedBrandbook = localStorage.getItem('tempBrandbook');
    if (savedBrandbook && !formDetails.brandbook) {
      try {
        const parsedBrandbook = JSON.parse(savedBrandbook);
        // Update form fields if available
        if (parsedBrandbook.companyName) {
          setFormDetails(prev => ({
            ...prev,
            companyName: parsedBrandbook.companyName,
            brandbook: parsedBrandbook
          }));
        } else {
          // Just set the brandbook if no company name
          setFormDetails(prev => ({
            ...prev,
            brandbook: parsedBrandbook
          }));
        }
      } catch (error) {
        console.error('Failed to parse saved brandbook from localStorage:', error);
      }
    }
  }, []);

  const updateBrandbook = (newBrandbook: BrandbookData) => {
    setFormDetails((prev) => ({
      ...prev,
      brandbook: newBrandbook,
    }));
  };

  const updateBrandbookSection = (section: string, content: any) => {
    setFormDetails((prev) => {
      if (!prev.brandbook) return prev;

      let updatedSection;
      switch (section) {
        case 'brandvalues':
          updatedSection = { brandValues: content };
          break;
        case 'productservice':
          updatedSection = { productService: content };
          break;
        case 'colorpalette':
          updatedSection = { colorPalette: content };
          break;
        default:
          updatedSection = { [section]: content };
      }

      return {
        ...prev,
        brandbook: {
          ...prev.brandbook,
          ...updatedSection
        }
      };
    });
  };

  return (
    <BrandBookContext.Provider
      value={{ formDetails, setFormDetails, updateBrandbook, updateBrandbookSection }}
    >
      {children}
    </BrandBookContext.Provider>
  );
};

export default BrandBookContextProvider;
